import { useEffect, useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Zap, HelpCircle } from "lucide-react";

/** A 3-field MT5 entry block:
 *    1. Broker dropdown (Exness / Vantage / XM / IC / FBS / FXTM / OctaFX / Other)
 *    2. Server picker — driven by selected broker. Free text only when broker='Other'.
 *    3. Login + Password fields
 *    + "Validate" button that probes the credentials against MetaApi BEFORE save.
 *
 *  Pure controlled component — parent owns broker/server/login/pwd state.
 *  This keeps it drop-in for both the new-account form and the resubmit form. */
export default function MT5CredentialsFields({
  broker, setBroker,
  server, setServer,
  loginId, setLoginId,
  pwd, setPwd,
  testidPrefix = "mt5",
  showValidate = true,
}) {
  const [presets, setPresets] = useState([]);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${AFF_API}/affiliate/mt5/broker-servers`, { headers: getAffHeaders() });
        if (alive) setPresets(r.data?.presets || []);
      } catch (e) {
        // Non-critical: presets are a nice-to-have autocomplete for
        // broker server names. Fields remain usable in free-text mode.
        console.debug("[MT5CredentialsFields] presets load failed:", e?.response?.status);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Match the loaded preset to the current broker (case-insensitive).
  const activePreset = presets.find(p => p.label.toLowerCase() === (broker || "").toLowerCase())
    || presets.find(p => p.key === (broker || "").toLowerCase())
    || presets.find(p => p.key === "other");
  const isOther = (activePreset?.key === "other");

  // When broker changes, auto-pick the first server in the list (unless already valid)
  useEffect(() => {
    if (!activePreset || isOther) return;
    if (server && activePreset.servers?.includes(server)) return;
    if (activePreset.servers?.length) setServer(activePreset.servers[0]);
     
  }, [activePreset?.key]);

  const validate = async () => {
    if (!loginId?.trim() || !server?.trim() || !pwd) {
      toast.error("Fill login, server and password first"); return;
    }
    setValidating(true);
    setResult(null);
    try {
      const r = await axios.post(`${AFF_API}/affiliate/mt5/validate`,
        { mt5_login: loginId.trim(), mt5_server: server.trim(), mt5_password: pwd },
        { headers: getAffHeaders(), timeout: 50000 }
      );
      setResult(r.data);
      if (r.data?.ok) toast.success("Credentials look good ✓");
      else toast.error(r.data?.error || "Validation failed");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Validation failed";
      setResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setValidating(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-credentials-block`}>
      <div className="grid grid-cols-2 gap-2">
        {/* Broker */}
        <div>
          <label className="text-[10px] text-zinc-500 mb-1 block">Broker</label>
          <select
            value={broker}
            onChange={(e) => { setBroker(e.target.value); setResult(null); }}
            data-testid={`${testidPrefix}-broker`}
            className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-white/10 text-xs text-white focus:outline-none focus:border-[#00E5FF]/40"
          >
            {presets.length === 0 && <option>Loading…</option>}
            {presets.map(p => (
              <option key={p.key} value={p.label}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* Server — dropdown for known brokers; free text for "Other" */}
        <div>
          <label className="text-[10px] text-zinc-500 mb-1 block flex items-center gap-1">
            MT5 Server
            {activePreset?.help && (
              <span className="text-zinc-600" title={activePreset.help}>
                <HelpCircle size={9} />
              </span>
            )}
          </label>
          {isOther ? (
            <input
              value={server}
              onChange={(e) => { setServer(e.target.value); setResult(null); }}
              required
              data-testid={`${testidPrefix}-server`}
              placeholder="e.g. MyBroker-MT5-Live"
              className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-white/10 text-xs text-white font-mono focus:outline-none focus:border-[#00E5FF]/40"
            />
          ) : (
            <select
              value={server || ""}
              onChange={(e) => { setServer(e.target.value); setResult(null); }}
              data-testid={`${testidPrefix}-server`}
              className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-white/10 text-xs text-white font-mono focus:outline-none focus:border-[#00E5FF]/40"
            >
              {(activePreset?.servers || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        {/* Login */}
        <div>
          <label className="text-[10px] text-zinc-500 mb-1 block">MT5 Login ID</label>
          <input
            value={loginId}
            onChange={(e) => { setLoginId(e.target.value); setResult(null); }}
            required
            data-testid={`${testidPrefix}-login`}
            placeholder="e.g. 87654321"
            className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-white/10 text-xs text-white font-mono focus:outline-none focus:border-[#00E5FF]/40"
          />
        </div>

        {/* Password */}
        <div>
          <label className="text-[10px] text-zinc-500 mb-1 block">MT5 Password (Main)</label>
          <input
            type="password"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); setResult(null); }}
            required
            data-testid={`${testidPrefix}-password`}
            placeholder="Main trading password"
            className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-white/10 text-xs text-white focus:outline-none focus:border-[#00E5FF]/40"
          />
        </div>
      </div>

      {/* Validate button + result */}
      {showValidate && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            data-testid={`${testidPrefix}-validate`}
            onClick={validate}
            disabled={validating || !loginId || !pwd || !server}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-40"
          >
            <Zap size={11} /> {validating ? "Validating… (up to 60s)" : "Validate MT5 Connection"}
          </button>
          {result?.ok && (
            <span data-testid={`${testidPrefix}-validate-ok`} className="inline-flex items-center gap-1 text-[10px] text-emerald-300">
              <CheckCircle2 size={11} /> Verified · broker connected
            </span>
          )}
          {result && !result.ok && (
            <span data-testid={`${testidPrefix}-validate-err`} className="text-[10px] text-rose-300 max-w-md text-right">
              <AlertTriangle size={10} className="inline mr-1" /> {result.error?.slice(0, 200)}
            </span>
          )}
        </div>
      )}
      {!presets.length && (
        <p className="text-[10px] text-zinc-600">Tip: Validate before paying to avoid copy-trading issues later.</p>
      )}
    </div>
  );
}
